import React, { useMemo } from "react";
import { ScrollView, TouchableOpacity, Text, Linking, View } from "react-native";
import LHOverlay from "../../../components/LHOverlay";
import lightStyles, {
  createHomeScreenOverlayContainerStyle,
  createHomeScreenThemeStyles,
} from "../HomeScreen.style";
import { TEXT } from "../../../constants/texts";
import LinkIcon from "./icons/LinkIcon";
import { useTheme } from "../../../theme";

interface ExternalLinkItem {
  id: string | number;
  linkTitle?: string;
  linkUrl?: string;
  title?: string;
  link?: {
    Description?: string;
    Url?: string;
  };
}

interface ExternalLinksOverlayProps {
  visible: boolean;
  onDismiss: () => void;
  externalLinks: ExternalLinkItem[] | string[];
}

const ExternalLinksOverlay: React.FC<ExternalLinksOverlayProps> = ({
  visible,
  onDismiss,
  externalLinks,
}) => {
  const { colors } = useTheme();
  const themeStyles = useMemo(() => createHomeScreenThemeStyles(colors), [colors]);
  const overlayContainerStyle = useMemo(
    () => createHomeScreenOverlayContainerStyle(colors),
    [colors]
  );
  const handleLinkPress = (url: string, description: string) => {
    if (url) {
      Linking.openURL(url).catch((err) => {
        console.error("An error occurred opening the link:", err);
        console.log(`Failed to open \"${description}\". Please try again later.`);
      });
    } else {
      console.log(`No URL available for: ${description}`);
    }
  };

  const isLinkObject = (item: ExternalLinkItem | string): item is ExternalLinkItem => {
    return typeof item === "object" && item !== null;
  };

  const hasData = externalLinks && externalLinks.length > 0;

  return (
    <LHOverlay
      visible={visible}
      onDismiss={onDismiss}
      title="All External Links"
      containerStyle={overlayContainerStyle}
      headerPaddingHorizontal={24}
      hideFooter={true}
      size="large"
    >
      <ScrollView style={lightStyles.overlayContent}>
        {hasData ? (
          externalLinks.map((link, index) => {
            if (isLinkObject(link)) {
              const url = link.linkUrl || link.link?.Url || "";
              const displayPrimary = link.linkTitle || link.link?.Description || link.title || "";

              return (
                <View key={link.id || index}>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={lightStyles.linkRow}
                    onPress={() => {
                      if (url) {
                        handleLinkPress(url, displayPrimary);
                      } else {
                        console.log(`Link clicked: ${displayPrimary}`);
                      }
                    }}
                  >
                    <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                      <LinkIcon size={16} color="#005CBC" />
                    </View>
                    <View style={lightStyles.linkTextContainer}>
                      <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{displayPrimary}</Text>
                    </View>
                  </TouchableOpacity>
                  <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
                </View>
              );
            }

            return (
              <View key={index}>
                <TouchableOpacity
                  activeOpacity={0.8}
                  style={lightStyles.linkRow}
                  onPress={() => console.log(`Link clicked: ${link}`)}
                >
                  <View style={[lightStyles.linkIconContainer, themeStyles.linkIconContainer]}>
                    <LinkIcon size={16} color="#005CBC" />
                  </View>
                  <View style={lightStyles.linkTextContainer}>
                    <Text style={[lightStyles.linkTextPrimary, themeStyles.linkTextPrimary]}>{link}</Text>
                  </View>
                </TouchableOpacity>
                <View style={[lightStyles.linkDivider, themeStyles.linkDivider]} />
              </View>
            );
          })
        ) : (
          <View style={lightStyles.noDataContainer}>
            <Text style={[lightStyles.noDataText, themeStyles.noDataText]}>{TEXT.COMMON.NO_DATA}</Text>
          </View>
        )}
      </ScrollView>
    </LHOverlay>
  );
};

export default ExternalLinksOverlay;
